import React from 'react';
import { Box } from '@mui/material';

import { Page4Aging } from './types';
import { wsrColors, wsrMonoFont } from './wsrTokens';

interface AgingBarsProps {
  incidents: Page4Aging;
  serviceRequests: Page4Aging;
}

interface AgingGroup {
  name: string;
  data: Page4Aging;
}

const COLORS = {
  d0_7: wsrColors.green,
  d8_14: wsrColors.amber,
  d14: wsrColors.red,
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
    <Box
      component="span"
      sx={{ width: 10, height: 10, background: color, borderRadius: '2px', display: 'inline-block' }}
    />
    {label}
  </Box>
);

const AgingBars: React.FC<AgingBarsProps> = ({ incidents, serviceRequests }) => {
  const groups: AgingGroup[] = [
    { name: 'Incidents', data: incidents },
    { name: 'Service Requests', data: serviceRequests },
  ];

  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${wsrColors.slate200}`,
        borderRadius: '12px',
        padding: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '14px',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <Box sx={{ fontSize: 15, fontWeight: 600 }}>Outstanding by Age</Box>
        <Box sx={{ display: 'flex', gap: '14px', fontSize: 12, color: wsrColors.slate500 }}>
          <Legend color={COLORS.d0_7} label="0–7 days" />
          <Legend color={COLORS.d8_14} label="8–14 days" />
          <Legend color={COLORS.d14} label=">14 days" />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {groups.map((group) => {
          const total = group.data.total || 1;
          return (
            <Box key={group.name}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13,
                  marginBottom: '6px',
                }}
              >
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {group.name}
                </Box>
                <Box component="span" sx={{ fontFamily: wsrMonoFont, color: wsrColors.slate500 }}>
                  {group.data.total} open
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  height: 14,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  background: wsrColors.slate100,
                }}
              >
                <Box sx={{ width: `${(group.data.d0_7 / total) * 100}%`, background: COLORS.d0_7 }} />
                <Box sx={{ width: `${(group.data.d8_14 / total) * 100}%`, background: COLORS.d8_14 }} />
                <Box sx={{ width: `${(group.data.d14 / total) * 100}%`, background: COLORS.d14 }} />
              </Box>
              <Box
                sx={{
                  fontFamily: wsrMonoFont,
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: wsrColors.slate500,
                  marginTop: '4px',
                }}
              >
                <Box component="span">{group.data.d0_7} fresh</Box>
                <Box component="span">{group.data.d8_14} aging</Box>
                <Box component="span" sx={{ color: COLORS.d14, fontWeight: 600 }}>
                  {group.data.d14} stale
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AgingBars;
